import React, { useState } from 'react';
import { Icon } from './icons';
import { fmt, money } from './format';
import { accountDisplayBalance, accountMaskSuffix } from './dataAdapters';
import { disconnectReasonText } from '../lib/connectionStatus';

function IncludeToggle({ included, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={included}
      title={included ? 'Counted in cash balance' : 'Excluded from cash balance'}
      onClick={(e) => { e.stopPropagation(); onChange(!included); }}
      className="pressable"
      style={{
        width: 40, height: 24, borderRadius: 99, flexShrink: 0,
        background: included ? 'var(--accent)' : 'var(--line)',
        position: 'relative', transition: 'background 0.18s',
      }}
    >
      <span style={{
        position: 'absolute', top: 3, left: included ? 19 : 3,
        width: 18, height: 18, borderRadius: 99, background: 'var(--bg)',
        boxShadow: '0 1px 3px rgba(26,29,27,0.25)', transition: 'left 0.18s',
      }} />
    </button>
  );
}

function ManualAccountRow({ account, showBorder, onRename, onEditBalance, onDelete, onToggleIncluded }) {
  const [editing, setEditing] = useState(false);
  const [nameVal, setNameVal] = useState(account.name);
  const [balVal, setBalVal] = useState(String(accountDisplayBalance(account)));

  const startEdit = () => {
    setNameVal(account.name);
    setBalVal(String(accountDisplayBalance(account)));
    setEditing(true);
  };

  const save = () => {
    const trimmedName = nameVal.trim();
    const parsed = parseFloat(balVal);
    if (trimmedName) onRename(account.id, trimmedName);
    if (Number.isFinite(parsed)) onEditBalance(account.id, parsed);
    setEditing(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <div className="row-line" style={{ alignItems: 'center', gap: 10 }}>
        <span className="iconwrap icon-surface" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0 }}>
          <Icon name="wallet" size={18} />
        </span>
        <input autoFocus value={nameVal} onChange={(e) => setNameVal(e.target.value)} onKeyDown={onKeyDown} className="field" style={{ flex: 1 }} />
        <input value={balVal} onChange={(e) => setBalVal(e.target.value)} onKeyDown={onKeyDown} inputMode="decimal" className="field" style={{ width: 100, textAlign: 'right' }} />
        <button type="button" onClick={save} className="pressable" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>Save</button>
      </div>
    );
  }

  return (
    <div className="row-line" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 140px', minWidth: 0 }}>
        <span className="iconwrap icon-surface" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0 }}>
          <Icon name="wallet" size={18} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="title" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{account.name}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>Manual</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginLeft: 'auto' }}>
        <span className="num" style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)', minWidth: 72, textAlign: 'right' }}>
          {money(accountDisplayBalance(account))}
        </span>
        <IncludeToggle included={account.includeInBalance !== false} onChange={(next) => onToggleIncluded(account.id, next)} />
        <button type="button" onClick={startEdit} className="pressable iconwrap" style={{ width: 30, height: 30, borderRadius: 8, color: 'var(--ink-3)' }}>
          <Icon name="edit" size={16} />
        </button>
        <button type="button" onClick={() => onDelete(account.id)} className="pressable iconwrap" style={{ width: 30, height: 30, borderRadius: 8, color: 'var(--ink-3)' }}>
          <Icon name="trash" size={16} />
        </button>
      </div>
    </div>
  );
}

export function AccountsView({
  accounts = [],
  cards = [],
  balance,
  bankConnections,
  bankSyncing,
  plaidReady,
  onConnectBank,
  onSyncBank,
  onReconnectBank,
  onRemoveBank,
  onReCategorizeAccount,
  recategorizingAccountId,
  uncategorizedByAccount,
  onAddManualAccount,
  onRenameManualAccount,
  onEditManualBalance,
  onDeleteManualAccount,
  onToggleAccountIncluded,
  /// Card id -> amount of a payment that has posted on the card but not yet
  /// left the bank. Annotates the row; never changes the balance shown.
  pendingCardPayments = {},
  desktop = true,
}) {
  const [addingManual, setAddingManual] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualBalance, setManualBalance] = useState('');

  const totalAssets = accounts
    .filter((a) => (a.type === 'depository' || a.type === 'manual') && a.includeInBalance !== false)
    .reduce((s, a) => s + accountDisplayBalance(a), 0);
  const totalLiabilities = cards.reduce((s, c) => s + (c.owed || 0), 0);
  const netWorth = totalAssets - totalLiabilities;
  const excludedAccounts = accounts.filter((a) => (a.type === 'depository' || a.type === 'manual') && a.includeInBalance === false);

  const depository = accounts.filter((a) => a.type === 'depository');
  const credit = accounts.filter((a) => a.type === 'credit');
  const manual = accounts.filter((a) => a.type === 'manual');

  const submitManual = () => {
    const trimmed = manualName.trim();
    if (!trimmed) return;
    const parsed = parseFloat(manualBalance);
    onAddManualAccount(trimmed, Number.isFinite(parsed) ? parsed : 0);
    setManualName('');
    setManualBalance('');
    setAddingManual(false);
  };

  const onAddKeyDown = (e) => {
    if (e.key === 'Enter') submitManual();
    if (e.key === 'Escape') setAddingManual(false);
  };

  const AccountRow = ({ account, icon, iconStyle, amount, amountColor, showBorder, showToggle, note }) => {
    const uncategorized = uncategorizedByAccount ? uncategorizedByAccount(account.id) : 0;
    const categorizing = recategorizingAccountId === account.id;
    return (
      <div className="row-line" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 180px', minWidth: 0 }}>
          <span className="iconwrap icon-surface" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, ...iconStyle }}>
            <Icon name={icon} size={18} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="title" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{account.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {account.institution || account.subtype || (account.type === 'credit' ? 'Credit' : 'Depository')}
              {accountMaskSuffix(account.name, account.lastFour)}
              {uncategorized > 0 ? ` · ${uncategorized} uncategorized` : ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginLeft: 'auto' }}>
          {onReCategorizeAccount && (
            <button
              type="button"
              onClick={() => onReCategorizeAccount(account.id)}
              disabled={!!recategorizingAccountId || bankSyncing}
              className="pressable chip"
              style={{ fontSize: 11, opacity: recategorizingAccountId && !categorizing ? 0.45 : 1 }}
              title="Re-run auto-categorization for this account"
            >
              {categorizing ? '…' : 'Re-categorize'}
            </button>
          )}
          <span className="num" style={{ fontSize: 14.5, fontWeight: 600, color: amountColor || 'var(--ink)', minWidth: 72, textAlign: 'right' }}>
            {money(amount)}
          </span>
          {showToggle && (
            <IncludeToggle included={account.includeInBalance !== false} onChange={(next) => onToggleAccountIncluded(account.id, next)} />
          )}
        </div>
        {/* Subordinate to the balance, never replacing it: the figure above is
            exactly what the bank reported, and this says what we know is about
            to change it. Adjusting the headline instead would show a number no
            bank ever reported and would double-count once the debit posts. */}
        {note && (
          <div style={{
            flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: 'var(--accent)', fontWeight: 600,
          }}>
            <Icon name="repeat" size={13} sw={2.2} />
            <span>{note}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: desktop ? 'minmax(0, 0.9fr) minmax(0, 1.3fr)' : 'minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* The last gradient card in the app. A dark green slab with white
            type was the one surface that didn't take the page's ink, didn't
            invert on dark, and put the app's largest figure on a background
            no other figure sits on. Net worth is a page figure like any
            other now, and the breakdown is four hairline rows. */}
        <div className="pg-section">
          <div className="eyebrow">Total net worth</div>
          <div className="pg-figure" style={{ color: netWorth < 0 ? 'var(--alarm)' : 'var(--ink)' }}>
            {money(netWorth, { cents: false })}
          </div>
          {excludedAccounts.length > 0 && (
            <div
              title={excludedAccounts.map((a) => a.name).join(', ')}
              className="pg-note"
              style={{ display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <Icon name="info" size={12} />
              Excludes {excludedAccounts.length} account{excludedAccounts.length === 1 ? '' : 's'}
            </div>
          )}
        </div>

        <div className="pg-section">
          <div className="eyebrow">Breakdown</div>
          {/* Three rows, not four: the old layout showed "Liabilities" in the
              hero and "Credit owed" below it, which are the same figure under
              two names. Cash is the row that earns its place — the spendable
              slice of assets. */}
          {[
            { label: 'Assets', val: totalAssets },
            { label: 'of which cash', val: balance },
            { label: 'Liabilities', val: totalLiabilities },
          ].map((item) => (
            <div key={item.label} className="row-line" style={{ alignItems: 'baseline' }}>
              <span style={{ flex: 1, fontSize: 14.5, fontWeight: 500, color: 'var(--ink)' }}>{item.label}</span>
              <span className="num" style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink)' }}>{money(item.val, { cents: false })}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 10 }}>
          {/* Global sync already lives in the sidebar/header on every page — no need to repeat it here. */}
          <button
            type="button"
            onClick={plaidReady ? onConnectBank : undefined}
            disabled={!plaidReady}
            className="btn-primary pressable"
            style={{ opacity: plaidReady ? 1 : 0.5 }}
          >
            <Icon name="plus" size={15} sw={2.4} />
            Link account
          </button>
        </div>

        {(depository.length > 0 || manual.length > 0) && onToggleAccountIncluded && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', margin: '-6px 2px 0' }}>
            Use the switch on an account to include or exclude it from your cash balance and forecast.
          </div>
        )}

        {depository.length > 0 && (
          <div className="pg-section">
            <div className="eyebrow">Bank accounts</div>
            {depository.map((a, i) => (
              <AccountRow key={a.id} account={a} icon="bank" amount={accountDisplayBalance(a)} showBorder={i > 0} showToggle />
            ))}
          </div>
        )}

        {onAddManualAccount && (
          <div className="pg-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="eyebrow">Manual accounts</span>
              {!addingManual && (
                <button
                  type="button"
                  onClick={() => setAddingManual(true)}
                  className="pressable chip"
                  style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Icon name="plus" size={13} sw={2.4} /> Add
                </button>
              )}
            </div>
            {addingManual && (
              <div className="row-line" style={{ alignItems: 'center', gap: 10 }}>
                <input
                  autoFocus
                  placeholder="Account name"
                  value={manualName}
                  onChange={(e) => setManualName(e.target.value)}
                  onKeyDown={onAddKeyDown}
                  className="field" style={{ flex: 1 }}
                />
                <input
                  placeholder="0.00"
                  value={manualBalance}
                  onChange={(e) => setManualBalance(e.target.value)}
                  onKeyDown={onAddKeyDown}
                  inputMode="decimal"
                  className="field" style={{ width: 100, textAlign: 'right' }}
                />
                <button
                  type="button"
                  onClick={submitManual}
                  className="pressable iconwrap"
                  style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--ink)', color: 'var(--bg)', flexShrink: 0 }}
                >
                  <Icon name="check" size={16} />
                </button>
              </div>
            )}
            {manual.length === 0 && !addingManual ? (
              <p className="pg-note">No manual accounts yet. Add cash, a savings jar, or anything not linked to a bank.</p>
            ) : (
              manual.map((a, i) => (
                <ManualAccountRow
                  key={a.id}
                  account={a}
                  showBorder={i > 0 || addingManual}
                  onRename={onRenameManualAccount}
                  onEditBalance={onEditManualBalance}
                  onDelete={onDeleteManualAccount}
                  onToggleIncluded={onToggleAccountIncluded}
                />
              ))
            )}
          </div>
        )}

        {credit.length > 0 && (
          <div className="pg-section">
            <div className="eyebrow">Credit cards</div>
            {credit.map((a, i) => {
              const pending = pendingCardPayments[a.id];
              return (
                <AccountRow
                  key={a.id}
                  account={a}
                  icon="card"
                  amount={cards.find((c) => c.id === a.id)?.owed ?? accountDisplayBalance(a)}
                  showBorder={i > 0}
                  note={pending ? `${fmt(pending, { cents: false })} payment pending` : null}
                />
              );
            })}
          </div>
        )}

        <div className="pg-section">
          <div className="eyebrow">Connections</div>
          {bankConnections.length === 0 ? (
            <p className="pg-note">No bank connections yet. Link an account to auto-sync transactions.</p>
          ) : (
            bankConnections.map((enr, i) => {
              // Connections without provider 'plaid' predate the move to Plaid.
              // Their provider is retired, so they can never sync again and
              // only offer the migration path to Plaid.
              const defunct = enr.provider !== 'plaid';
              const disconnected = defunct || enr.status === 'disconnected';
              const lastSyncedDate = enr.lastSynced ? new Date(enr.lastSynced) : null;
              const staleDays = lastSyncedDate ? (Date.now() - lastSyncedDate.getTime()) / 86400000 : 0;
              const stale = !disconnected && staleDays > 3;
              const syncedLabel = lastSyncedDate
                ? `Synced ${lastSyncedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'Never synced';
              const disconnectReason = disconnected ? disconnectReasonText(enr.disconnectReason) : null;
              const statusColor = disconnected ? 'var(--neg)' : stale ? 'var(--warn)' : 'var(--ink-3)';
              return (
                <div key={enr.id} className="row-line" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '1 1 180px', minWidth: 0 }}>
                    <span className="iconwrap" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0, background: disconnected ? 'color-mix(in srgb,var(--neg) 12%,transparent)' : 'var(--accent-soft)', color: disconnected ? 'var(--neg)' : 'var(--accent)' }}>
                      <Icon name="bank" size={18} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{enr.accountLabel || enr.institution || 'Bank account'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: statusColor, fontWeight: disconnected || stale ? 600 : 400 }}>
                        <span style={{ width: 7, height: 7, borderRadius: 99, background: disconnected ? 'var(--neg)' : stale ? 'var(--warn)' : 'var(--ink-3)', flexShrink: 0 }} />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {defunct
                            ? 'Retired provider — re-link with Plaid'
                            : disconnected
                              ? `Needs reconnecting — ${disconnectReason || 'syncing stopped'}`
                              : stale
                                ? `${syncedLabel} — may be stale`
                                : syncedLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 12, marginLeft: 'auto' }}>
                    {onReconnectBank && (
                      disconnected ? (
                        <button type="button" onClick={() => onReconnectBank(enr)} className="pressable" style={{ height: 32, padding: '0 14px', borderRadius: 9, background: 'var(--neg)', color: 'var(--bg)', fontSize: 12, fontWeight: 600 }}>
                          {defunct ? 'Fix' : 'Reconnect'}
                        </button>
                      ) : (
                        <button type="button" onClick={() => onReconnectBank(enr)} className="pressable chip" title="Re-authenticate this bank connection with Plaid" style={{ fontSize: 12 }}>
                          Reconnect
                        </button>
                      )
                    )}
                    {!disconnected && (
                      <button type="button" onClick={() => onSyncBank(enr)} disabled={bankSyncing} className="pressable chip" style={{ fontSize: 12 }}>
                        Sync
                      </button>
                    )}
                    <button type="button" onClick={() => onRemoveBank(enr.id)} className="pressable iconwrap" style={{ width: 32, height: 32, color: 'var(--neg)', flexShrink: 0 }}>
                      <Icon name="trash" size={15} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>
    </div>
  );
}
